import React, { Suspense, lazy, useEffect, useState, useCallback } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import './css/mobile.css'
import NavBar from "./components/NavBar";
import NavBarV2 from "./components/NavBarV2";
import RouteTracker from "./components/utils/RouteTracker";
import RouteSEO from "./components/utils/RouteSEO";
import ProtectedRoute from "./components/utils/ProtectedRoute";
import HomeOld from "./components/routes/Home-old";
import { fetchMe } from "./components/utils/user";


import {
  hasAnyAuthToken,
  refreshAccessToken,
  SITE,
  MAIN_API_URL,
  BILLING_API_URL,
  isLoggedIn,
} from "./components/utils/auth";
import { loadPlanLimits } from "./components/utils/planLimits";
import StaffRoute from "./components/utils/StaffRoute";
import AdminNavBar from "./components/admin/AdminNav";
import { HelmetProvider } from "react-helmet-async";

const SignIn = lazy(() => import("./components/routes/SignIn"));
const SignUp = lazy(() => import("./components/routes/Signup"));
const WriterDen = lazy(() => import("./components/routes/pompt/WritersDen"));
const AuthorsDesk = lazy(() => import("./components/routes/AuthorsDesk"));
const Library = lazy(() => import("./components/routes/Library"));
const Dashboard = lazy(() => import("./components/routes/Dashboard"));
const FreeDashboard = lazy(() => import("./components/routes/FreeDashboard"));
const ViewAllCreatedPrompts = lazy(() => import("./components/routes/ViewAllCreatedPrompts"));
const ViewGiftOverview = lazy(() => import("./components/routes/ViewGiftOverview"));
const BuyQuills = lazy(() => import("./components/routes/BuyQuills"));
const BuyInks = lazy(() => import("./components/routes/BuyInks"));
const NewBook = lazy(() => import("./components/routes/book/NewBook"));
const EditChapter = lazy(() => import("./components/routes/book/chapter/EditChapter"));
const BookDetails = lazy(() => import("./components/routes/book/BookDetails"));
const BookSettings = lazy(() => import("./components/routes/book/bookSettings/BookSettings"));
const RoadMapHome = lazy(() => import("./components/routes/book/roadmap/RoadMapHome"));
const CharacterDetails = lazy(() => import("./components/routes/book/roadmap/character/CharacterDetails"));
const CharacterList = lazy(() => import("./components/routes/book/roadmap/character/CharacterList"));
const Upgrade = lazy(() => import("./components/routes/Upgrade"));
const EditSubplot = lazy(() => import("./components/routes/book/roadmap/plot/EditSubplot"));
const OneTimePurchase = lazy(() => import("./components/routes/OneTimePurchase"));
const ReadBook = lazy(() => import("./components/routes/book/ReadBook"));
const PolicyPage = lazy(() => import("./components/PolicyPage"));
const ViewPrompt = lazy(() => import("./components/routes/pompt/ViewPrompt"));
const AdminSupportTickets = lazy(() => import("./components/admin/AdminSupportTickets"));
const NotFound = lazy(() => import("./components/routes/NotFound"));
const FollowListPage = lazy(() => import("./components/routes/FollowListPage"));
const Unauthorized = lazy(() => import("./components/routes/Unauthorized"));
const AdminBooks = lazy(() => import("./components/admin/AdminBooks"));
const AdminGifts = lazy(() => import("./components/admin/AdminGifts"));
const Blocklist = lazy(() => import("./components/routes/settings/Blocklist"));
const Safety = lazy(() => import("./components/routes/settings/Safety"));
const AllSubplots = lazy(() => import("./components/routes/book/roadmap/plot/AllSubplots"));
const GeneralSettings = lazy(() => import("./components/routes/settings/GeneralSettings"));
const PrivacySettings = lazy(() => import("./components/routes/settings/PrivacySettings"));
const AdminReportDetail = lazy(() => import("./components/admin/AdminReportDetail"));
const StoryEngine = lazy(() => import("./components/routes/book/StoryEngine"));
const AdminPrompts = lazy(() => import("./components/admin/AdminPrompts"));
const SupportSettings = lazy(() => import("./components/routes/settings/SupportSettings"));
const WorldBuilderPage = lazy(() => import("./components/routes/book/worldBuilder/WorldBuilderHome"));
const Preview = lazy(() => import("./components/routes/book/Preview"));
const MapView = lazy(() => import("./components/routes/book/worldBuilder/MapView"));
const AdminDashboard = lazy(() => import("./components/admin/AdminDashboard"));
const AdminLogin = lazy(() => import("./components/admin/AdminLogin"));
const AdminUsers = lazy(() => import("./components/admin/AdminUser"));
const AdminUserDetail = lazy(() => import("./components/admin/AdminUserDetail"));
const AdminPlans = lazy(() => import("./components/admin/AdminPlans"));
const AdminPlansAll = lazy(() => import("./components/admin/AdminPlansAll"));
const ForgotPassword = lazy(() => import("./components/routes/ForgotPassword"));
const ForgotPasswordEmail = lazy(() => import("./components/routes/ForgotPasswordEmail"));
const ResetPassword = lazy(() => import("./components/routes/ResetPassword"));
const VerifyEmail = lazy(() => import("./components/routes/VerifyEmail"));
const VerifyEmailNotice = lazy(() => import("./components/routes/VerifyEmailNotice"));
const AdminSupportTicketDetail = lazy(() => import("./components/admin/AdminSupportTicketDetail"));
const GeneratorsHub = lazy(() => import("./components/routes/tools/GeneratorsHub"));
const AdminAchievements = lazy(() => import("./components/admin/AdminAchievements"));
const ViewReadlist = lazy(() => import("./components/routes/ViewReadlist"));
const ViewAchievements = lazy(() => import("./components/routes/ViewAchievements"));
const ViewNotifications = lazy(() => import("./components/routes/ViewNotifications"));
const NotificationSettings = lazy(() => import("./components/routes/settings/NotificationSettings"));
const UserProfileView = lazy(() => import("./components/routes/UserProfileView"));
const ViewConversations = lazy(() => import("./components/routes/ViewConversations"));
const ViewConversationDetail = lazy(() => import("./components/routes/ViewConversationDetail"));
const BookStats = lazy(() => import("./components/routes/book/BookStats"));
const AdminUserReports = lazy(() => import("./components/admin/AdminUserReports"));
const AdminTransactions = lazy(() => import("./components/admin/AdminTransactions"));
const AdminSiteSettings = lazy(() => import("./components/admin/AdminSiteSettings"));
const AdminNotifications = lazy(() => import("./components/admin/AdminNotifications"));
const CreatorProgramSetup = lazy(() => import("./components/routes/settings/CreatorProgramSetup"));
const BookNotesView = lazy(() => import("./components/routes/book/BookNotesView"));
const MoodBoard = lazy(() => import("./components/routes/book/MoodBoard"));
const LoreEncyclopedia = lazy(() => import("./components/routes/book/LoreEncyclopedia"));
const StoryHealthOverview = lazy(() => import("./components/routes/book/StoryHealthOverview"));
const CharacterRelationshipWeb = lazy(() => import("./components/routes/book/roadmap/character/CharacterRelationshipWeb"));
const CharacterArcBoard = lazy(() => import("./components/routes/book/roadmap/character/CharacterArcBoard"));
const ScenePlanner = lazy(() => import("./components/routes/book/roadmap/ScenePlanner"));
const DraftComparison = lazy(() => import("./components/routes/book/DraftComparison"));
const CheckoutCancel = lazy(() => import("./components/routes/CheckoutCancel"));
const CheckoutSuccess = lazy(() => import("./components/routes/CheckoutSuccess"));
const ConflictGenerator = lazy(() => import("./components/routes/book/ConflictGenerator"));
const ThemeSymbolTracker = lazy(() => import("./components/routes/book/ThemeSymbolTracker"));
const ResearchArchive = lazy(() => import("./components/routes/book/ResearchArchive"));
const ContinuityTracker = lazy(() => import("./components/routes/book/ContinuityTracker"));
const StoryCalendar = lazy(() => import("./components/routes/book/StoryCalendar"));
const BookSprintDetails = lazy(() => import("./components/routes/book/BookSprintDetails"));
const ManageSubscription = lazy(() => import("./components/routes/ManageSubscription"));
const AppAchievementToast = lazy(() => import("./components/AppAchievementToast"));
const AppNotificationToast = lazy(() => import("./components/AppNotificationToast"));

function toBool(value) {
  if (value === true || value === false) return value;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1" || v === "yes";
  }
  if (typeof value === "number") {
    return value === 1;
  }
  return false;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [userPlan, setUserPlan] = useState(null);
  const [siteInfo, setSiteInfo] = useState(null);
  const [inV1, setInV1] = useState(true);
  const [isStaff, setIsStaff] = useState(false);
  const [userSite, setUserSite] = useState("");
  const [authReady, setAuthReady] = useState(false);
  const [hidePastDueBanner, setHidePastDueBanner] = useState(false);

  useEffect(() => {
    if (location.pathname !== "/" || hasAnyAuthToken()) {
      import("./flatly.min.css");
    }
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    async function fetchSiteInfo() {
      try {
        const res = await fetch(`${MAIN_API_URL}/site-info/?site=${SITE}`);
        const data = await res.json();

        if (!cancelled) {
          const version = String(data?.version ?? "v1.0");
          const isV1Version = /^v1(?:\.\d+)*$/i.test(version);
          setInV1(isV1Version);
          setSiteInfo(data);
        }
      } catch (err) {
        console.error("Error fetching site info:", err);
      }
    }

    fetchSiteInfo();

    return () => {
      cancelled = true;
    };
  }, []);

  const clearAuthStorage = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    localStorage.removeItem("user_id");
    localStorage.removeItem("fb_plan");
    localStorage.removeItem("is_staff");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("user_site");

    setIsStaff(false);
    setUserPlan(null);
    setUserSite("");
  }, []);

 const fetchUserPlan = useCallback(async () => {
  try {
    const response = await fetch(`${BILLING_API_URL}/me/plan/?site=${SITE}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Plan fetch failed with status ${response.status}`);
    }

    const data = await response.json();
    const plan = data?.plan || null;

    const planKey =
      plan?.key ||
      plan?.name ||
      localStorage.getItem("fb_plan") ||
      "draft";

    const planDisplay =
      plan?.display_name ||
      plan?.name ||
      localStorage.getItem("fb_plan") ||
      "Draft";

    setUserPlan({
      ...plan,
      key: planKey,
      name: plan?.name || planKey,
      display_name: planDisplay,
    });

    localStorage.setItem("fb_plan", planKey);
  } catch (err) {
    console.error("Error fetching user plan:", err);

    const fallbackKey = localStorage.getItem("fb_plan") || "draft";
    const fallbackDisplay = localStorage.getItem("fb_plan") || "Draft";

    setUserPlan({
      key: fallbackKey,
      name: fallbackKey,
      display_name: fallbackDisplay,
    });
  }
}, []);

  useEffect(() => {
    let mounted = true;

    async function bootstrapAuth() {
      setAuthReady(false);
      setIsStaff(false);
      setUserSite("");

      if (!hasAnyAuthToken()) {
        if (mounted) {
          setIsStaff(false);
          setUserPlan(null);
          setUserSite("");
          setAuthReady(true);
        }
        return;
      }

      try {
        const freshAccess = await refreshAccessToken();

        if (!freshAccess) {
          throw new Error("Unable to refresh access token.");
        }

        const me = await fetchMe();
        try {
          await loadPlanLimits();
        await fetchUserPlan();
        } catch (planErr) {
          console.warn("Plan fetch failed, using fallback plan:", planErr);
        }
        

        if (!mounted) return;

        const normalizedUserSite = String(me?.site || "").trim().toLowerCase();
        const normalizedIsStaff = toBool(me?.is_staff);
        const normalizedIsSuperuser = toBool(me?.is_superuser);

        setIsStaff(normalizedIsStaff);
        setUserSite(normalizedUserSite);

        localStorage.setItem("is_staff", normalizedIsStaff ? "1" : "0");
        localStorage.setItem("is_superuser", normalizedIsSuperuser ? "1" : "0");
        localStorage.setItem("user_site", normalizedUserSite);

        if (me?.user_id) {
          localStorage.setItem("user_id", me.user_id);
        }

        if (me?.user_plan) {
          localStorage.setItem("fb_plan", me.user_plan);

          setUserPlan((prev) => ({
            ...(prev || {}),
            key: prev?.key || me.user_plan,
            name: prev?.name || me.user_plan,
            display_name:
              prev?.display_name ||
              localStorage.getItem("fb_plan") ||
              me.user_plan,
          }));
        }
      } catch (err) {
        console.error("Failed to initialize auth:", err);
        clearAuthStorage();
      } finally {
        if (mounted) {
          setAuthReady(true);
        }
      }
    }

    bootstrapAuth();

    const interval = setInterval(() => {
      if (hasAnyAuthToken()) {
        refreshAccessToken().catch((err) => {
          console.error("Token refresh failed:", err);
        });
      }
    }, 4 * 60 * 1000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [fetchUserPlan, clearAuthStorage]);

  const NavComponent = inV1 ? NavBar : NavBarV2;
  const HomeComponent = inV1 ? HomeOld : HomeOld;
  const normalizedPlan = String(
    userPlan?.key ||
    userPlan?.name ||
    localStorage.getItem("fb_plan") ||
    "draft"
  ).trim().toLowerCase();

  const subscriptionStatus = String(
    userPlan?.subscription_status || ""
  )
    .trim()
    .toLowerCase();

  const masterAuthorPlans = new Set([
  "master author",
  "master_author",
  "gold",
  "gold_monthly",
  "gold_yearly",
]);

const dashboardElement = masterAuthorPlans.has(normalizedPlan)
  ? <Dashboard />
  : <FreeDashboard />; 

  const hideNav = ["/tos", "/privacy", "/login", "/register", "/admin/login"].includes(
    location.pathname
  );
  const isAdminRoute = location.pathname.startsWith("/admin");
  const showPastDueBanner =
    isLoggedIn() &&
    authReady &&
    subscriptionStatus === "past_due" &&
    !hidePastDueBanner &&
    !isAdminRoute;
  const allowPublicUserNav = ["/", "/tools/generators"].includes(location.pathname);

  const showAdminNav =
    !hideNav &&
    isAdminRoute &&
    authReady &&
    isLoggedIn() &&
    isStaff &&
    userSite === "mother";

  const showUserNav =
    (isLoggedIn() || allowPublicUserNav) &&
    !hideNav &&
    !showAdminNav;

  return (
    <HelmetProvider>
      <>
      <div className="bg-blobs" />

      <div className="App app-shell">
        {showAdminNav ? (
          <AdminNavBar site_name={siteInfo?.site_name} />
        ) : (
          showUserNav && <NavComponent site_name={siteInfo?.site_name} />
        )}

        {showPastDueBanner && (
          <div className="pd-past-due-banner">
            <div>
              <strong>Payment issue</strong>
              <span>
                We could not process your subscription payment. Stripe will retry, but your paid access may end if payment is not updated.
              </span>
            </div>

            <button
              type="button"
              className="pd-past-due-action"
              onClick={() => navigate("/settings/general")}
            >
              Manage Billing
            </button>

            <button
              type="button"
              className="pd-past-due-close"
              onClick={() => setHidePastDueBanner(true)}
              aria-label="Dismiss payment warning"
            >
              ×
            </button>
          </div>
        )}

        <RouteTracker />
        <RouteSEO />

        <div className="app-main">
          {isLoggedIn() && (
            <Suspense fallback={null}>
              <AppAchievementToast />
              <AppNotificationToast />
            </Suspense>
          )}
          <Suspense fallback={<div className="route-loading" aria-live="polite">Loading...</div>}>
          <Routes>
            <Route
              path="/"
              element={isLoggedIn() ? <Navigate to="/authorsDesk" replace /> : <HomeComponent />}
            />

            <Route path="/register" element={<SignUp />} />
            <Route path="/login" element={<SignIn inV1={inV1} />} />
            <Route path="/tos" element={<PolicyPage type="tos" />} />
            <Route path="/privacy" element={<PolicyPage type="privacy" />} />
            <Route path="/mother" element={<PolicyPage type="mother" />} />
            <Route path="/tools/generators" element={<GeneratorsHub />} />
            <Route
              path="/creator-program"
              element={<PolicyPage type="creator_program" />}
            />

            <Route
              path="/community-guidelines"
              element={<PolicyPage type="community_guidelines" />}
            />

            <Route
              path="/copyright"
              element={<PolicyPage type="copyright" />}
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute authReady={authReady}>
                  {dashboardElement}
                </ProtectedRoute>
              }
            />

            <Route
              path="/creator-program/setup"
              element={
                <ProtectedRoute authReady={authReady}>
                  <CreatorProgramSetup />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/achievements"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ViewAchievements />
                </ProtectedRoute>
              }
            />

            <Route
              path="/books/:bookId/subplots"
              element={
                <ProtectedRoute authReady={authReady}>
                  <AllSubplots />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/sprint-details"
              element={
                <ProtectedRoute authReady={authReady}>
                  <BookSprintDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/account/subscription"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ManageSubscription />
                </ProtectedRoute>
              }
            />


            <Route
              path="/dashboard/readlist"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ViewReadlist />
                </ProtectedRoute>
              }
            />

            <Route
              path="/notifications"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ViewNotifications />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/prompts"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ViewAllCreatedPrompts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/gifts"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ViewGiftOverview />
                </ProtectedRoute>
              }
            />

            <Route
              path="/quills"
              element={
                <ProtectedRoute authReady={authReady}>
                  <BuyQuills />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inks"
              element={
                <ProtectedRoute authReady={authReady}>
                  <BuyInks />
                </ProtectedRoute>
              }
            />

            <Route
              path="/writersDen"
              element={
                <ProtectedRoute authReady={authReady}>
                  <WriterDen />
                </ProtectedRoute>
              }
            />

            <Route
              path="/library"
              element={
                <ProtectedRoute authReady={authReady}>
                  <Library site_name={siteInfo?.site_name} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/prompt/:promptId"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ViewPrompt />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/story-engine"
              element={
                <ProtectedRoute authReady={authReady}>
                  <StoryEngine />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/draft-comparison/:chapterId"
              element={
                <ProtectedRoute authReady={authReady}>
                  <DraftComparison />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/stats"
              element={
                <ProtectedRoute authReady={authReady}>
                  <BookStats />
                </ProtectedRoute>
              }
            />

            <Route
              path="/upgrade"
              element={
                <ProtectedRoute authReady={authReady}>
                  <Upgrade userPlan={userPlan} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/purchase/book/upgrade"
              element={
                <ProtectedRoute authReady={authReady}>
                  <OneTimePurchase />
                </ProtectedRoute>
              }
            />

            <Route
              path="/purchase/book/:bookId/upgrade"
              element={
                <ProtectedRoute authReady={authReady}>
                  <OneTimePurchase />
                </ProtectedRoute>
              }
            />

            <Route
              path="/billing/cancel"
              element={
                <ProtectedRoute authReady={authReady}>
                  <CheckoutCancel />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile/:userId/:type"
              element={
                <ProtectedRoute authReady={authReady}>
                  <FollowListPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/library/:bookId/read"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ReadBook />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk"
              element={
                <ProtectedRoute authReady={authReady}>
                  <AuthorsDesk />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/new/book"
              element={
                <ProtectedRoute authReady={authReady}>
                  <NewBook />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/edit-chapter/:chapterId"
              element={
                <ProtectedRoute authReady={authReady}>
                  <EditChapter />
                </ProtectedRoute>
              }
            />

            <Route
              path="/messages"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ViewConversations />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/lore"
              element={
                <ProtectedRoute authReady={authReady}>
                  <LoreEncyclopedia />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/story-health"
              element={
                <ProtectedRoute authReady={authReady}>
                  <StoryHealthOverview />
                </ProtectedRoute>
              }
            />

            <Route
              path="/books/:bookId/scene-planner"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ScenePlanner />
                </ProtectedRoute>
              }
            />

            <Route
              path="/characters/:characterId/arc-board"
              element={
                <ProtectedRoute authReady={authReady}>
                  <CharacterArcBoard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/characters/:characterId/relationship-web"
              element={
                <ProtectedRoute authReady={authReady}>
                  <CharacterRelationshipWeb />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/mood-board"
              element={
                <ProtectedRoute authReady={authReady}>
                  <MoodBoard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/authorsDesk/book/:bookId/notes"
              element={
                <ProtectedRoute authReady={authReady}>
                  <BookNotesView />
                </ProtectedRoute>
              }
            />

            <Route
              path="/messages/:conversationId"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ViewConversationDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId"
              element={
                <ProtectedRoute authReady={authReady}>
                  <BookDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/:bookId/:previewCode"
              element={
                <ProtectedRoute authReady={authReady}>
                  <Preview />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/settings"
              element={
                <ProtectedRoute authReady={authReady}>
                  <BookSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/roadmap"
              element={
                <ProtectedRoute authReady={authReady}>
                  <RoadMapHome />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/conflict-generator"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ConflictGenerator />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/theme-symbols"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ThemeSymbolTracker />
                </ProtectedRoute>
              }
            />


            <Route
              path="/authorsDesk/book/details/:bookId/world-builder"
              element={
                <ProtectedRoute authReady={authReady}>
                  <WorldBuilderPage userPlan={userPlan} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/map/:mapId/"
              element={
                <ProtectedRoute authReady={authReady}>
                  <MapView />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/character/:characterId"
              element={
                <ProtectedRoute authReady={authReady}>
                  <CharacterDetails userPlan={userPlan} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/research-archive"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ResearchArchive />
                </ProtectedRoute>
              }
            />
            <Route
              path="/authorsDesk/book/details/:bookId/continuity"
              element={
                <ProtectedRoute authReady={authReady}>
                  <ContinuityTracker />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/details/:bookId/story-calendar"
              element={
                <ProtectedRoute authReady={authReady}>
                  <StoryCalendar />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/:bookId/characters"
              element={
                <ProtectedRoute authReady={authReady}>
                  <CharacterList userPlan={userPlan} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/authorsDesk/book/:bookId/subplot/:subplotId/edit"
              element={
                <ProtectedRoute authReady={authReady}>
                  <EditSubplot />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/support"
              element={
                <ProtectedRoute authReady={authReady}>
                  <SupportSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/privacy"
              element={
                <ProtectedRoute authReady={authReady}>
                  <PrivacySettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/general"
              element={
                <ProtectedRoute authReady={authReady}>
                  <GeneralSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/notifications"
              element={
                <ProtectedRoute authReady={authReady}>
                  <NotificationSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile/:userId"
              element={
                <ProtectedRoute authReady={authReady}>
                  <UserProfileView />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/support"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminSupportTickets />
                </StaffRoute>
              }
            />

            <Route
              path="/admin/books"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminBooks />
                </StaffRoute>
              }
            />

            <Route
              path="/admin/gifts"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminGifts />
                </StaffRoute>
              }
            />

            <Route
              path="/admin/transactions"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminTransactions />
                </StaffRoute>
              }
            />
            <Route
              path="/admin/reports/:reportId"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminReportDetail />
                </StaffRoute>
              }
            />
            <Route
              path="/admin/users/:userId/reports"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminUserReports />
                </StaffRoute>
              }
            />
            <Route
              path="/admin/prompts"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminPrompts />
                </StaffRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminSiteSettings />
                </StaffRoute>
              }
            />

            <Route
              path="/admin/notifications"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminNotifications />
                </StaffRoute>
              }
            />

            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/forgot-password/email" element={<ForgotPasswordEmail />} />
            <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
            <Route path="/verify-email-notice" element={<VerifyEmailNotice />} />
            <Route path="/verify-email/:userId/:code" element={<VerifyEmail />} />

            

            <Route
              path="/billing/success"
              element={
                <ProtectedRoute authReady={authReady}>
                  <CheckoutSuccess />
                </ProtectedRoute>
              }
            />

            <Route path="/admin/login" element={<AdminLogin />} />

            <Route
              path="/admin"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminDashboard />
                </StaffRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminUsers />
                </StaffRoute>
              }
            />

            <Route
              path="/admin/support/:id"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminSupportTicketDetail />
                </StaffRoute>
              }
            />

            <Route
              path="/admin/users/:userId"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminUserDetail />
                </StaffRoute>
              }
            />

            <Route
              path="/settings/privacy/blocked"
              element={
                <ProtectedRoute authReady={authReady}>
                  <Blocklist />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/safety"
              element={
                <ProtectedRoute authReady={authReady}>
                  <Safety />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/plans"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminPlans />
                </StaffRoute>
              }
            />

            <Route
              path="/admin/plans/all"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminPlansAll />
                </StaffRoute>
              }
            />
            <Route
              path="/admin/achievements"
              element={
                <StaffRoute authReady={authReady}>
                  <AdminAchievements />
                </StaffRoute>
              }
            />

            <Route path="/401" element={<Unauthorized />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </div>

        {!isAdminRoute && (
          <footer className="site-footer">
            <span>
              © {new Date().getFullYear()} Plot Division {"|"}{" "}
              <a
                href="https://southcentraldev.com"
                target="_blank"
                rel="noreferrer"
              >
                Site made by South Central Dev
              </a>
            </span>

            <div className="site-footer-links">
              <Link to="/tos">Terms</Link>

              <span className="site-footer-divider">•</span>

              <Link to="/privacy">Privacy</Link>

              <span className="site-footer-divider">•</span>

              <Link to="/creator-program">Creator Program</Link>

              <span className="site-footer-divider">•</span>

              <Link to="/community-guidelines">Guidelines</Link>

              <span className="site-footer-divider">•</span>

              <Link to="/copyright">Copyright</Link>

              <span className="site-footer-divider">•</span>

              <Link to="/settings/support">Support</Link>
            </div>
          </footer>
        )}
      </div>
    </>
    </HelmetProvider>
  );
}

export default App;
